import { isAddress } from "ethers";
import type {
  ChainConfig,
  Environment,
  ResolvedSurfConfig,
  SurfConfig,
  TokenConfig,
} from "../types";
import { SurfError, SurfErrorCode } from "./errors";

export function assertAddress(value: string, fieldName: string): void {
  if (!isAddress(value)) {
    throw new SurfError(
      SurfErrorCode.INVALID_CONFIG,
      `${fieldName} must be a valid EVM address`,
    );
  }
}

export function validateTokenConfig(token: TokenConfig): void {
  assertAddress(token.address, `${token.symbol}.address`);
  token.vaultAddresses.forEach((vaultAddress, index) => {
    assertAddress(vaultAddress, `${token.symbol}.vaultAddresses[${index}]`);
  });
}

export function validateChainConfig(chain: ChainConfig): void {
  assertAddress(chain.factoryAddress, `${chain.name}.factoryAddress`);
  assertAddress(chain.wethAddress, `${chain.name}.wethAddress`);
  chain.tokens.forEach(validateTokenConfig);
}

export function validateEnvironment(value: string): asserts value is Environment {
  if (value !== "mainnet" && value !== "testnet") {
    throw new SurfError(
      SurfErrorCode.INVALID_ENVIRONMENT,
      `Unsupported environment: ${value}`,
    );
  }
}

export function validateConfig(
  config: SurfConfig,
  chainExists: (environment: Environment, chainId: number) => boolean,
  defaultChainIdForEnvironment: (environment: Environment) => number,
): ResolvedSurfConfig {
  const resolvedProjectId = config.appId ?? config.projectId;
  if (!config.projectName || !resolvedProjectId) {
    throw new SurfError(
      SurfErrorCode.MISSING_PROJECT_ID,
      "projectName and appId/projectId are required",
    );
  }

  const environment = config.environment ?? "mainnet";
  validateEnvironment(environment);

  const chainId = config.chainId ?? defaultChainIdForEnvironment(environment);
  if (!chainExists(environment, chainId)) {
    throw new SurfError(
      SurfErrorCode.UNSUPPORTED_CHAIN,
      `Chain ${chainId} is not registered for environment ${environment}`,
    );
  }

  return {
    projectName: config.projectName,
    projectId: resolvedProjectId,
    appId: resolvedProjectId,
    environment,
    chainId,
    rpcUrl: config.rpcUrl ?? "",
    apiBaseUrl: config.apiBaseUrl ?? "https://api.surfliquid.com",
    factoryAddress: (config.factoryAddress ?? "0x0000000000000000000000000000000000000000") as `0x${string}`,
    autoApprove: config.autoApprove ?? true,
    logger: config.logger ?? "error",
  };
}
